import {
  type DisplayAdPlacement,
  type DisplayAdsContext,
  DisplayAdsManager,
  type DisplayAdsPageTargetingInput,
} from '@tunein/display-ads';
import { Singleton, type SingletonInstance } from '@tunein/web-utils';
import { api } from 'src/client/api';
import { player } from 'src/client/player';
import { buildConsentData } from 'src/client/player/utils';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { defaultLanguageCode } from 'src/common/constants/locale';
import { selectFirstVisitStartTime, selectOneTrust } from 'src/common/selectors/app';
import { DEPLOYMENT_ENV } from 'src/common/utils/environment';

type DisplayAdsInitOptions = {
  locale?: string;
};

function getDisplayAdsContext(): DisplayAdsContext {
  return {
    firstVisitTimestampMs: selectFirstVisitStartTime(storeSingleton.state),
  };
}

// The `env` targeting key reflects the DEPLOYMENT environment (legacy parity: Mint maps
// `State.env` the same way), not the Platform-services tier — a local build against stage
// APIs must still target as `dev`.
const deploymentEnvTargetingMap: Record<string, string> = {
  local: 'dev',
  experimental: 'dev',
  pr: 'dev',
  development: 'dev',
  qa: 'dev',
  stage: 'stage',
  production: 'prod',
};

function getInitialPageTargeting(locale?: string): DisplayAdsPageTargetingInput {
  return {
    env: deploymentEnvTargetingMap[DEPLOYMENT_ENV] || 'prod',
    language: (locale || defaultLanguageCode).substring(0, 2),
  };
}

export class DisplayAdsSingleton extends Singleton<DisplayAdsManager> {
  #unsubscribeStore?: () => void;

  init(options: DisplayAdsInitOptions = {}) {
    if (this.isReady) {
      return true;
    }

    if (api.isPremium) {
      return false;
    }

    api.telemetry.logInfo({
      message: '[Display Ads] Init',
    });

    try {
      this.instance = new DisplayAdsManager({
        api: api.instance,
        playerManager: player,
        context: getDisplayAdsContext(),
        pageTargeting: getInitialPageTargeting(options.locale),
      });
    } catch (error) {
      api.telemetry.logError({
        message: '[Display Ads] Init Failed',
        context: { error },
      });

      return false;
    }

    this.#setConsentData();
    this.#setupListeners();
    api.telemetry.logInfo({
      message: '[Display Ads] Init Done',
    });

    return true;
  }

  async setContext(context: DisplayAdsContext) {
    await this.ready;

    this.instance.setContext(context);
  }

  async setPageTargeting(pageTargeting: DisplayAdsPageTargetingInput) {
    await this.ready;

    this.instance.setPageTargeting(pageTargeting);
  }

  async refresh(placements?: DisplayAdPlacement[]) {
    await this.ready;

    this.instance.refresh(placements);
  }

  #setConsentData() {
    buildConsentData()
      .then((consentData) => {
        if (this.isReady) {
          this.instance.setConsent(consentData);
        }
      })
      .catch((error) => {
        api.telemetry.logError({
          message: '[Display Ads] Consent Update Failed',
          context: { error },
        });
      });
  }

  #setupListeners() {
    if (this.#unsubscribeStore) {
      return;
    }

    let oneTrust = selectOneTrust(storeSingleton.state);

    this.#unsubscribeStore = storeSingleton.subscribe(() => {
      const previousOneTrust = oneTrust;
      oneTrust = selectOneTrust(storeSingleton.state);

      if (previousOneTrust !== oneTrust) {
        this.#setConsentData();
      }
    });
  }
}

export const displayAds = new DisplayAdsSingleton() as SingletonInstance<
  DisplayAdsManager,
  DisplayAdsSingleton
>;
